import React, { useEffect, useMemo, useState } from "react";
import "../../design-tokens/tokens/tokens.css";
import "./Tabs.css";

interface TabsProps {
  className?: string;
  TabClassName?: string;
  ContentClassName?: string;
  initialTabIndex?: number;
  tabs: Array<React.ReactNode>;
  contents: Array<React.ReactNode>;
  hideTabs?: number[];
  testIds?: string[];
  onSelectTab: (index: number) => void;
  banners?: Array<React.ReactNode>;
}

const Tabs = ({
  className,
  initialTabIndex,
  tabs,
  TabClassName,
  ContentClassName,
  contents,
  hideTabs,
  onSelectTab,
  testIds,
  banners,
}: TabsProps) => {
  const tabsRef = React.useRef(null);
  const [activeTab, setActiveTab] = useState(initialTabIndex);
  const [focusTab, setFocusTab] = useState(initialTabIndex);

  useEffect(() => {
    setActiveTab(initialTabIndex);
    setFocusTab(initialTabIndex);
  }, [initialTabIndex]);

  const { tabsTitleList, tabsContentList, tabsTestIdList } = useMemo(() => {
    if (!hideTabs?.length) {
      return {
        tabsTitleList: tabs,
        tabsContentList: contents,
        tabsTestIdList: testIds,
      };
    }

    const tabsTitleList = tabs.filter((_, index) => !hideTabs.includes(index));
    const tabsContentList = contents.filter(
      (_, index) => !hideTabs.includes(index)
    );
    const tabsTestIdList = testIds?.filter(
      (_, index) => !hideTabs.includes(index)
    );

    return {
      tabsTitleList,
      tabsContentList,
      tabsTestIdList,
    };
  }, [tabs, contents, testIds, hideTabs]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        setFocusTab((prevFocus) => {
          const newFocus = (prevFocus + 1) % tabs.length;
          document.getElementById("tab-" + newFocus)?.focus();
          return newFocus;
        });
      } else if (event.key === "ArrowLeft") {
        setFocusTab((prevFocus) => {
          const newFocus = (prevFocus - 1 + tabs.length) % tabs.length;
          document.getElementById("tab-" + newFocus)?.focus();
          return newFocus;
        });
      } else if (event.code === "Space" || event.key === "Enter") {
        setActiveTab(focusTab);
        onSelectTab(focusTab);
      }
    };

    tabsRef.current?.addEventListener("keydown", handleKeydown);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tabsRef.current?.removeEventListener("keydown", handleKeydown);
    };
  }, [activeTab, focusTab, onSelectTab, tabs.length]);

  if (tabs.length !== contents.length) {
    throw new Error("Tabs and contents must be the same length");
  }

  return (
    <>
      <div
        role="tablist"
        className={`sr-tabs ${className || ""}`}
        ref={tabsRef}
      >
        {tabsTitleList.map((tab, index) => (
          <button
            id={"tab-" + index}
            key={"tab-" + index}
            className={`sr-tab ${TabClassName || ""} ${
              activeTab === index ? "sr-tab-active" : ""
            }`}
            type="button"
            role="tab"
            value={index}
            tabIndex={activeTab === index ? 0 : -1}
            onClick={() => {
              setActiveTab(index);
              onSelectTab(index);
            }}
            data-testid={
              tabsTestIdList ? tabsTestIdList[index] : `tab-${index}`
            }
          >
            {tab}
          </button>
        ))}
      </div>
      <div
        key={"content-" + activeTab}
        role="tabpanel"
        className={ContentClassName}
      >
        {banners}
        {tabsContentList[activeTab]}
      </div>
    </>
  );
};

export { Tabs };
export type { TabsProps };
